import { WorkerRequirementStatus } from "@clipboard-health/contract-worker-app-bff";
import { DialogContent } from "@mui/material";
import { BottomSheetHeader } from "@src/appV2/redesign/components/BottomSheetHeader";
import {
  BottomSheet,
  BottomSheetLoadingContent,
  type UseModalState,
} from "@src/appV2/redesign/system/ui-components";
import { getSafeAreaPaddings } from "@src/appV2/redesign/system/ui-theme";
import { useCallback, useRef } from "react";
import { generatePath, useHistory } from "react-router-dom";

import {
  DOCUMENT_DETAILS_FULL_PATH,
  DOCUMENT_STRIPE_VERIFICATION_REJECTED_PATH,
  DOCUMENT_SUBMISSION_APPROVED_PATH,
  DOCUMENT_SUBMISSION_IN_REVIEW_PATH,
} from "../../../paths";
import { useGetDocumentDetailsPageRequirement } from "../../useGetDocumentDetailsPageRequirement";
import { useRequirementDetailsContext } from "../../useRequirementDetailsContext";

const MIN_POLLING_TIME_MS = 1000; // 1 second
const MAX_POLLING_TIME_MS = 10_000; // 10 seconds
const POLLING_INTERVAL_MS = 1000; // 1 second

interface DocumentDetailsStripeIdentityVerificationInProgressBottomSheetProps {
  modalState: UseModalState;
}

/*
  This bottom sheet is used to show the user that the document is being submitted.
  It will poll the worker requirement status and redirect to the appropriate bottom sheet
  when the status changes. Since our stripe verification is async, we need to poll to check the status.
  For most sessions, the status will update within the 5 seconds, so we poll to give a better user experience.

  It follows the following strategy:
  - Wait 1 second before polling
  - Poll every second, up to a limit.

  When polling:
  - If the status is approved, it will redirect to the generic approved bottom sheet.
  - If the status is rejected, it will redirect to the personal id specific rejected bottom sheet.
  - If the status is in review, keep polling until the limit is reached, then redirect to the in review bottom sheet.
*/
export function DocumentDetailsStripeIdentityVerificationInProgressBottomSheet(
  props: DocumentDetailsStripeIdentityVerificationInProgressBottomSheetProps
) {
  const { modalState } = props;
  const history = useHistory();
  const startedAt = useRef(Date.now());

  const { requirement } = useRequirementDetailsContext();
  const { requirementId } = requirement;

  const handleClose = useCallback(() => {
    modalState.closeModal();
  }, [modalState]);

  useGetDocumentDetailsPageRequirement({
    requirementId,
    options: {
      refetchInterval: (data) => {
        const elapsed = Date.now() - startedAt.current;
        const status = data?.[0]?.status;

        // Wait at least 1 second before considering redirection
        if (elapsed >= MIN_POLLING_TIME_MS) {
          if (status === WorkerRequirementStatus.APPROVED) {
            history.replace(
              generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_APPROVED_PATH}`, {
                requirementId,
              })
            );
            return false; // Stop polling
          }

          if (status === WorkerRequirementStatus.REJECTED) {
            history.replace(
              generatePath(
                `${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_STRIPE_VERIFICATION_REJECTED_PATH}`,
                {
                  requirementId,
                }
              )
            );
            return false; // Stop polling
          }

          if (elapsed >= MAX_POLLING_TIME_MS) {
            // Redirect to in-review if status is still pending and time is up
            history.replace(
              generatePath(`${DOCUMENT_DETAILS_FULL_PATH}/${DOCUMENT_SUBMISSION_IN_REVIEW_PATH}`, {
                requirementId,
              })
            );
            return false; // Stop polling
          }
        }

        // Continue polling
        return POLLING_INTERVAL_MS;
      },
    },
  });

  return (
    <BottomSheet
      header={<BottomSheetHeader onClose={handleClose} />}
      modalState={modalState}
      paperSx={{
        ...getSafeAreaPaddings({ bottom: true }),
      }}
    >
      <DialogContent sx={{ paddingX: 5 }}>
        <BottomSheetLoadingContent
          title="Submitting Document"
          description="Should only take a few seconds"
        />
      </DialogContent>
    </BottomSheet>
  );
}
